import { useEffect, useRef, useState } from 'react'
import { Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { isOrganisationPrefillEnabled } from '@/constants'
import {
  searchOrganisationMatches,
  type OrganisationPrefillMatch,
} from '@/lib/organisation-prefill-api'

const SEARCH_DEBOUNCE_MS = 350

type OrganisationNameLookupFieldProps = {
  id: string
  label?: string
  placeholder?: string
  value: string
  onChange: (name: string) => void
  /** Company website domain picked from a match; `null` when the user has not picked one. */
  domain: string | null
  onDomainChange: (domain: string | null) => void
  onSubmit?: () => void
  disabled?: boolean
  focusOnMount?: boolean
}

function MatchIcon({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) {
    return (
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Building2 className="size-4" aria-hidden />
      </span>
    )
  }
  return (
    <img
      src={src}
      alt=""
      className="size-8 shrink-0 rounded-md bg-muted/40 object-contain"
      onError={() => setFailed(true)}
    />
  )
}

/** Organisation name input with company type-ahead when `features.organisationPrefill` is on. */
export function OrganisationNameLookupField({
  id,
  label = 'Organisation name',
  placeholder = 'Enter your company or team name',
  value,
  onChange,
  domain,
  onDomainChange,
  onSubmit,
  disabled = false,
  focusOnMount = false,
}: OrganisationNameLookupFieldProps) {
  const enabled = isOrganisationPrefillEnabled()
  const [matches, setMatches] = useState<OrganisationPrefillMatch[]>([])
  const [fromEmail, setFromEmail] = useState(false)
  const [searching, setSearching] = useState(false)
  const [typedQuery, setTypedQuery] = useState<string | null>(null)
  const selectedIconRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled || domain || value.trim()) return
    const controller = new AbortController()
    searchOrganisationMatches('', controller.signal)
      .then((data) => {
        if (data.email_domain && data.results.length) {
          setMatches(data.results)
          setFromEmail(true)
        }
      })
      .catch(() => {})
    return () => controller.abort()
    // Email-domain suggestions load once when the step opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  useEffect(() => {
    if (!enabled || typedQuery === null) return
    const query = typedQuery.trim()
    if (query.length < 2) {
      setMatches([])
      setSearching(false)
      return
    }
    const controller = new AbortController()
    setSearching(true)
    const timer = window.setTimeout(() => {
      searchOrganisationMatches(query, controller.signal)
        .then((data) => {
          setMatches(data.results)
          setFromEmail(false)
        })
        .catch(() => {})
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false)
        })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [enabled, typedQuery])

  const handleType = (next: string) => {
    onChange(next)
    if (!enabled) return
    if (domain) onDomainChange(null)
    setTypedQuery(next)
  }

  const handlePick = (match: OrganisationPrefillMatch) => {
    selectedIconRef.current = match.icon_url
    onChange(match.name)
    onDomainChange(match.domain)
    setMatches([])
    setTypedQuery(null)
  }

  const showMatches = enabled && !domain && matches.length > 0

  return (
    <div className="space-y-3">
      <FormField
        label={label}
        htmlFor={id}
        description={
          enabled && !domain
            ? 'Start typing and pick your company to fill in its details.'
            : undefined
        }
      >
        <Input
          id={id}
          value={value}
          onChange={(e) => handleType(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="organization"
          autoFocus={focusOnMount}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onSubmit) {
              e.preventDefault()
              onSubmit()
            }
          }}
        />
      </FormField>

      {enabled && searching && !showMatches ? (
        <p className="text-sm text-muted-foreground" role="status">
          Looking up companies…
        </p>
      ) : null}

      {showMatches ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground" role="status">
            {fromEmail ? 'Matches for your email domain' : 'Is it one of these?'}
          </p>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {matches.map((match) => (
              <li key={match.domain}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handlePick(match)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none disabled:opacity-50"
                >
                  <MatchIcon src={match.icon_url} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{match.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {match.domain}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {enabled && domain ? (
        <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
          <MatchIcon src={selectedIconRef.current} />
          <p className="min-w-0 flex-1 truncate text-sm">
            <span className="text-muted-foreground">Website </span>
            <span className="font-medium">{domain}</span>
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              selectedIconRef.current = null
              onDomainChange(null)
              setTypedQuery(value)
            }}
          >
            Change
          </Button>
        </div>
      ) : null}
    </div>
  )
}
